import {
  DomainError,
  type ReminderChannel,
  HU_VAT_RATES,
  buildLine,
  countedLines,
  currency,
  formatMoney,
  fromMinor,
  legalProfile,
  warrantyRequired,
  warrantyUntil,
  missingRequired,
  nextStatuses,
  workOrderNextStep,
  odometerFromInput,
  odometerWarning,
  stockStatus,
  toMinor,
  validateCustomer,
  type ItemKind,
  type PriceMode,
  type WorkOrderLineInput,
  type WorkOrderStatus,
  type VehicleInput,
} from "@szerviz/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Field, TextInput } from "../components/Field";
import { Help } from "../components/Help";
import { Dictate, appendDictation } from "../components/Dictate";
import { StockBadge, useStockTracking } from "./Inventory";
import { Photos } from "../components/Photos";
import { PaymentChoice, PaymentInvoice } from "../components/PaymentInvoice";
import { DeclinedWorkEmail, Recommendations } from "../components/Recommendations";
import { missingBuyerData } from "../../../../supabase/functions/_shared/szamlazz";
import { QUOTE_ANSWERED_EVENT } from "../components/QuoteNotices";
import { Plate } from "../components/Plate";
import { CloseReminderDates, CustomerReminderConsent, reminderContactMissing, reminderDatesChanged, reminderDatesOf, type ReminderDates } from "../components/Reminders";
import { MissingNote, useRequired } from "../components/Required";
import { can, useApp, useFeature } from "../context";
import type { Article, Customer, IntakeLink, Invoice, ItemSuggestion, PaymentMethod, Photo, QuoteChannel, QuoteRequest, Vehicle, WorkOrder, WorkOrderDetail, WorkOrderItem, VehiclePhotoResult, WorkOrderSignature } from "../data";
import { normalizePhone } from "../../../../supabase/functions/_shared/messaging";
import { VehicleForm } from "./Vehicles";
import { VehicleDataSheet } from "../components/VehicleDataSheet";
import { DueServices } from "../components/DueServices";
import { PhotoStart, takePendingPhoto } from "../components/VehiclePhoto";
import { WorkshopLogo, useWorkshopLogo } from "../components/WorkshopLogo";
import { PlanTag } from "../components/TrialGuide";
import { Icon } from "../components/Icon";
import { DocHead, DocTotals, KeyValues, MadeWith, Signatures, Stamp } from "../components/PrintDocs";
import { IntakePanel, IntakeStep, PrintCollage, SignButton, SignedNote, intakeLink, shotsOf, useIntakeMessage, useSignatureMatches, type IntakeShot, type LocalShot } from "../components/Intake";
import { intakeCollage, intakeMissing, intakeSettings, shortHash } from "@szerviz/core";
import { renderSVG } from "uqr";

const parseNumber = (s: string): number | null => {
  const t = s.replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

function useMoney() {
  const { i18n } = useTranslation();
  const { workshop } = useApp();
  return (minor: number) => formatMoney(minor, workshop.currency, i18n.language);
}

function useDate() {
  const { i18n } = useTranslation();
  // a bare date (YYYY-MM-DD) is read at noon so no time zone moves it to another day
  return (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : "";
}

/** Today's local date as YYYY-MM-DD. */
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Sums the stored line totals (imported lines keep the old program's amounts). */
function sumItems(items: WorkOrderItem[]) {
  const byRate = new Map<number, { net: number; vat: number }>();
  let net = 0;
  let vat = 0;
  let gross = 0;
  for (const i of items) {
    net += i.net;
    vat += i.vat;
    gross += i.gross;
    const r = byRate.get(i.vatRate) ?? { net: 0, vat: 0 };
    byRate.set(i.vatRate, { net: r.net + i.net, vat: r.vat + i.vat });
  }
  return { net, vat, gross, byVatRate: [...byRate.entries()].sort(([a], [b]) => b - a).map(([vatRate, r]) => ({ vatRate, ...r })) };
}

export function StatusChip({ status }: { status: WorkOrderStatus }) {
  const { t } = useTranslation();
  return <span className={`status status-${status}`}>{t(`workOrder.status.${status}`)}</span>;
}

/** What the work order waits for, coloured by urgency. */
export function NextStepChip({ order }: { order: WorkOrder }) {
  const { t } = useTranslation();
  const s = workOrderNextStep(order);
  return <span className={`next-step next-${s.tone}`}>{t(`nextStep.${s.key}`, { count: s.count ?? 0 })}</span>;
}

/** The work order at a glance: next step, lines and total, quote, deadline (the payment is in the closed band). */
function WorkOrderSummary({ order, items, quotes }: { order: WorkOrder; items: WorkOrderItem[]; quotes: QuoteRequest[] }) {
  const { t, i18n } = useTranslation();
  const counted = countedLines(items);
  const gross = counted.reduce((a, i) => a + i.gross, 0);
  const pending = items.filter((i) => i.approval === "pending").length;
  const lastQuote = quotes[quotes.length - 1];
  const day = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
  const parts = [
    t("summary.lines", { count: counted.length, gross: formatMoney(gross, "HUF", i18n.language) }),
    lastQuote ? (lastQuote.answeredAt ? t("summary.quoteAnswered", { date: day(lastQuote.answeredAt) }) : t("summary.quoteWaiting", { count: pending })) : null,
    order.expectedReadyAt && order.status !== "closed" ? t("summary.expected", { date: day(order.expectedReadyAt) }) : null,
  ].filter(Boolean);
  return (
    <div className="wo-summary" role="status">
      <NextStepChip order={{ ...order, itemCount: items.length, pendingApprovals: pending }} />
      <span className="muted">{parts.join(" · ")}</span>
    </div>
  );
}

/** Work orders as a list; compact = inside a vehicle or customer page. */
export function WorkOrderList({ rows, compact }: { rows: WorkOrder[] | null; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { go } = useApp();
  const money = useMoney();
  const date = useDate();
  if (rows === null) return <p>{t("common.loading")}</p>;
  if (rows.length === 0) return <p className="muted">{t(compact ? "workOrders.noneYet" : "common.noResults")}</p>;
  const open = (id: string) => go("workOrders", id);
  return (
    <div className="table-scroll">
    <table className="list">
      <thead>
        <tr>
          <th>{t("workOrder.number")}</th>
          <th>{t("workOrder.status")}</th>
          <th>{t("nextStep.column")}</th>
          <th>{t("vehicle.plateNumber")}</th>
          <th>{t("workOrder.customer")}</th>
          <th>{t("workOrder.date")}</th>
          <th className="num">{t("workOrder.odometerKm")}</th>
          <th className="num">{t("workOrder.gross")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id} onClick={() => open(o.id)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && open(o.id)}>
            <td>
              <strong>{o.number}</strong>
              {o.needsReview && <span className="review-dot" title={t("workOrder.needsReview")} aria-label={t("workOrder.needsReview")} />}
            </td>
            <td>
              <StatusChip status={o.status} />
            </td>
            <td data-label={t("nextStep.column")}>
              <NextStepChip order={o} />
            </td>
            <td>
              <Plate plate={o.plateNumber ?? ""} country={o.plateCountry} size="sm" />
              {o.vehicleLabel && <div className="muted small">{o.vehicleLabel}</div>}
            </td>
            <td>{o.customerName}</td>
            <td className="nowrap">{date(o.receivedAt)}</td>
            <td className="num" data-label={t("workOrder.odometerKm")}>{o.odometerKm === null || o.odometerKm === undefined ? "–" : `${o.odometerKm.toLocaleString(i18n.language)} km`}</td>
            <td className="num">{money(o.gross ?? 0)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/** On the vehicle page: previous work orders and a button for a new one. */
export function VehicleWorkOrders({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation();
  const { store, go } = useApp();
  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  useEffect(() => {
    void store.workOrdersOfVehicle(vehicleId).then(setRows);
  }, [store, vehicleId]);
  return (
    <div className="wo-on-vehicle">
      <div className="page-head">
        <h2>{t("workOrders.title")}</h2>
        <button type="button" className="primary" onClick={() => go("workOrders", `new:${vehicleId}`)}>
          {t("workOrders.newForVehicle")}
        </button>
      </div>
      <WorkOrderList rows={rows} compact />
    </div>
  );
}

export function WorkOrders({ openId }: { openId?: string }) {
  if (openId?.startsWith("new")) return <NewWorkOrder vehicleId={openId.split(":")[1]} />;
  if (openId) return <WorkOrderView key={openId} id={openId} />;
  return <WorkOrderSearch />;
}

function WorkOrderSearch() {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"active" | "all">("active");
  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const h = setTimeout(() => {
      store.searchWorkOrders(workshop.id, query, filter).then(setRows, setError);
    }, 200);
    return () => clearTimeout(h);
  }, [store, workshop.id, query, filter]);

  return (
    <section>
      <div className="page-head">
        <h1>
          {t("workOrders.title")} <Help id="workOrders.page" />
        </h1>
        <button type="button" className="primary" onClick={() => go("workOrders", "new")}>
          {t("workOrders.new")}
        </button>
      </div>
      <div className="toolbar">
        <input
          className="search"
          type="search"
          placeholder={t("workOrders.searchPlaceholder")}
          aria-label={t("common.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="segmented" role="group" aria-label={t("workOrders.filter")}>
          <button type="button" aria-pressed={filter === "active"} onClick={() => setFilter("active")}>
            {t("workOrders.filterActive")}
          </button>
          <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
            {t("workOrders.filterAll")}
          </button>
        </div>
      </div>
      <ErrorText error={error} />
      {filter === "active" && rows?.length === 0 ? (
        <div className="info">
          <p>{t("workOrders.noneActive")}</p>
          <button type="button" onClick={() => setFilter("all")}>
            {t("workOrders.showClosed")}
          </button>
        </div>
      ) : (
        <WorkOrderList rows={rows} />
      )}
    </section>
  );
}

/** New work order: 1. vehicle, 2. customer, 3. odometer and the customer's request. */
function NewWorkOrder({ vehicleId }: { vehicleId?: string }) {
  const { t } = useTranslation();
  const { store, workshop, go, back } = useApp();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [plate, setPlate] = useState("");
  const [found, setFound] = useState<Vehicle[]>([]);
  const [newVehicle, setNewVehicle] = useState(false);
  // work order from the registration photo: the read result fills the new vehicle form
  const [photo, setPhoto] = useState<VehiclePhotoResult | null>(null);
  // after the photo the odometer is the next thing to type
  const [jumpKm, setJumpKm] = useState(false);
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quickCustomer, setQuickCustomer] = useState(false);
  const [km, setKm] = useState("");
  const [lastKm, setLastKm] = useState<number | null>(null);
  const [request, setRequest] = useState("");
  const [readyAt, setReadyAt] = useState(todayIso);
  const [previous, setPrevious] = useState<WorkOrder[] | null>(null);
  // due services chosen to be added when the work order is created
  const [duePicked, setDuePicked] = useState<Record<string, WorkOrderLineInput[]>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // intake with photos: the photos stay on the phone until the work order is saved, then they are uploaded
  const intakeOn = useFeature("intakePhotos");
  const signOn = useFeature("phoneSignature");
  const intake = intakeSettings(workshop.general);
  const [shots, setShots] = useState<LocalShot[]>([]);
  const [uploading, setUploading] = useState<number | null>(null);
  const intakeMessage = useIntakeMessage();

  const chooseVehicle = useCallback(
    async (v: Vehicle) => {
      setVehicle(v);
      setDuePicked({});
      setNewVehicle(false);
      setCustomer(v.customerId ? { id: v.customerId, name: v.customerName ?? "" } : null);
      setLastKm(await store.lastOdometer(v.id));
      setPrevious(await store.workOrdersOfVehicle(v.id));
    },
    [store],
  );

  // a photo already read on the home screen
  useEffect(() => {
    const r = takePendingPhoto();
    if (r) {
      setPhoto(r);
      setNewVehicle(true);
      setJumpKm(true);
    }
  }, []);
  const photoFound = useCallback(
    (v: Vehicle) => {
      setJumpKm(true);
      void chooseVehicle(v);
    },
    [chooseVehicle],
  );

  useEffect(() => {
    if (!vehicleId) return;
    setJumpKm(true);
    void store.getVehicle(vehicleId).then((v) => v && chooseVehicle(v));
  }, [vehicleId, store, chooseVehicle]);

  useEffect(() => {
    if (vehicle || !plate.trim()) {
      setFound([]);
      return;
    }
    const h = setTimeout(() => void store.searchVehicles(workshop.id, plate, 8).then(setFound), 200);
    return () => clearTimeout(h);
  }, [plate, vehicle, store, workshop.id]);

  useEffect(() => {
    if (!customerQuery.trim()) {
      setCustomers([]);
      return;
    }
    const h = setTimeout(() => void store.searchCustomers(workshop.id, customerQuery, 8).then(setCustomers), 200);
    return () => clearTimeout(h);
  }, [customerQuery, store, workshop.id]);

  const kmValue = parseNumber(km);
  const input = {
    vehicleId: vehicle?.id ?? null,
    customerId: customer?.id ?? null,
    odometerKm: odometerFromInput(kmValue),
    expectedReadyAt: readyAt || null,
    customerRequest: request,
  };
  const missing = missingRequired("workOrder", input, workshop.requiredFields);
  const readyRequired = useRequired("workOrder", input, true).isReq("expectedReadyAt");
  // the lower reading warning shows only once the field is left, never while typing
  const [kmLeft, setKmLeft] = useState<{ v: typeof kmValue } | null>(null);
  const warning = kmLeft !== null && kmLeft.v === kmValue ? odometerWarning(input.odometerKm, lastKm) : null;
  // required mode: the Save button stays grey until enough intake photos are taken
  const photosMissing = intakeOn ? intakeMissing(intake, shots.length) : null;

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (missing.length > 0 || photosMissing) return setError(new DomainError("REQUIRED_MISSING"));
    setBusy(true);
    let sendAfterSign = false;
    try {
      const o = await store.createWorkOrder(workshop.id, input);
      for (const line of Object.values(duePicked).flat()) await store.saveWorkOrderItem(workshop.id, o.id, line);
      if (intakeOn && shots.length > 0) {
        // a photo that cannot be uploaded now can be taken again on the work order (its panel shows what is missing)
        let left = shots.length;
        setUploading(left);
        let uploaded = 0;
        for (const shot of shots) {
          try {
            await store.addPhoto(workshop.id, { vehicleId: o.vehicleId ?? null, workOrderId: o.id, kind: "intake", intakeView: shot.view }, shot);
            uploaded += 1;
          } catch {
            // counted below
          }
          setUploading(--left);
        }
        // the customer's personal link by e-mail, when the workshop wants it and the customer has an address.
        // With the phone signature on, it goes when the signing step ends on the new work order (signed: the
        // information e-mail; closed without signing: the one that mentions the objection), otherwise now.
        if (intake.send === "ifEmail" && uploaded > 0 && o.customerId) {
          try {
            const c = await store.getCustomer(o.customerId);
            if (c?.email) {
              if (signOn) sendAfterSign = true;
              else {
                const token = await store.createIntakeLink(o.id);
                await store.sendIntakeEmail(o.id, intakeMessage(o, c.name, intakeLink(token), uploaded, false));
              }
            }
          } catch {
            // not sent: the work order's intake panel says so and has a button to send it
          }
        }
      }
      // the customer is still at the counter: the signature pad opens on the new work order
      if (signOn) markAutoSign(o.id, sendAfterSign);
      go("workOrders", o.id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="wo-new">
      <div className="page-head">
        <button type="button" className="link" onClick={() => back("workOrders")}>
          ← {t("common.back")}
        </button>
        <h1>{t("workOrders.new")}</h1>
      </div>

      <div className="step">
        <h2>
          <span className="step-no">1</span> {t("workOrder.vehicle")}
        </h2>
        {vehicle ? (
          <div className="picked">
            <Plate plate={vehicle.plateNumber} country={vehicle.plateCountry} size="lg" />
            <span>{[vehicle.make, vehicle.model].filter(Boolean).join(" ")}</span>
            <button type="button" onClick={() => { setVehicle(null); setPrevious(null); setLastKm(null); }}>
              {t("workOrder.change")}
            </button>
          </div>
        ) : newVehicle ? (
          <VehicleForm
            key={photo ? "photo" : "typed"}
            initial={photo ? ({ plateNumber: "", ...photo.fields } as VehicleInput) : { plateNumber: plate.toUpperCase() }}
            photo={photo}
            onDone={(v) => {
              setPhoto(null);
              if (v) void chooseVehicle(v);
              else setNewVehicle(false);
            }}
          />
        ) : (
          <>
            <PhotoStart
              onFound={photoFound}
              onNew={(r) => {
                setPhoto(r);
                setNewVehicle(true);
                setJumpKm(true);
              }}
            />
            <input
              className="search plate-input"
              type="search"
              autoFocus
              placeholder={t("workOrder.platePlaceholder")}
              aria-label={t("vehicle.plateNumber")}
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
            />
            <ul className="pick-list">
              {found.map((v) => (
                <li key={v.id}>
                  <button type="button" onClick={() => void chooseVehicle(v)}>
                    <Plate plate={v.plateNumber} country={v.plateCountry} size="sm" /> {[v.make, v.model].filter(Boolean).join(" ")}
                    <span className="muted"> · {v.customerName ?? t("vehicle.noOwner")}</span>
                  </button>
                </li>
              ))}
            </ul>
            {plate.trim() && (
              <button type="button" onClick={() => setNewVehicle(true)}>
                + {t("workOrder.newVehicle")}
              </button>
            )}
          </>
        )}
        {previous && previous.length > 0 && (
          <div className="previous">
            <h3>{t("workOrder.previous")}</h3>
            <WorkOrderList rows={previous.slice(0, 5)} compact />
          </div>
        )}
      </div>

      {vehicle && (
        <form className="step" onSubmit={create} noValidate>
          <h2>
            <span className="step-no">2</span> {t("workOrder.customer")}
          </h2>
          {customer ? (
            <div className="picked">
              <strong>{customer.name}</strong>
              <button type="button" onClick={() => setCustomer(null)}>
                {t("workOrder.change")}
              </button>
            </div>
          ) : quickCustomer ? (
            <QuickCustomer
              onDone={(c) => {
                setQuickCustomer(false);
                if (c) setCustomer({ id: c.id, name: c.name });
              }}
            />
          ) : (
            <>
              <input
                type="search"
                className="search"
                placeholder={t("customers.searchPlaceholder")}
                aria-label={t("workOrder.customer")}
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
              <ul className="pick-list">
                {customers.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setCustomer({ id: c.id, name: c.name })}>
                      <strong>{c.name}</strong> <span className="muted">{c.city}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setQuickCustomer(true)}>
                + {t("customers.new")}
              </button>
            </>
          )}

          {intakeOn && <IntakeStep no={3} shots={shots} setShots={setShots} />}

          <h2>
            <span className="step-no">{intakeOn ? 4 : 3}</span> {t("workOrder.intake")}
          </h2>
          <div className="form-grid">
            <TextInput
              label={t("workOrder.odometerKm")}
              value={km}
              onChange={setKm}
              onBlur={() => setKmLeft({ v: kmValue })}
              type="text"
              autoFocus={jumpKm}
              required
              invalid={!!km && input.odometerKm === null}
              help="workOrder.odometerKm"
            />
            <TextInput
              label={t("workOrder.expectedReadyAt")}
              value={readyAt}
              onChange={setReadyAt}
              type="date"
              required={readyRequired}
              invalid={missing.some((d) => d.field === "expectedReadyAt")}
              help="workOrder.expectedReadyAt"
            />
            {warning && (
              <p className="warn wide" role="status">
                {t("workOrder.odometerLower", { last: String(lastKm) })}
              </p>
            )}
            <DueServices
              vehicle={vehicle}
              km={input.odometerKm}
              picked={Object.keys(duePicked)}
              onPick={(key, lines) =>
                setDuePicked((p) => {
                  const { [key]: _drop, ...rest } = p;
                  return lines ? { ...rest, [key]: lines } : rest;
                })
              }
            />
            <Field label={t("workOrder.customerRequest")} wide help="workOrder.customerRequest">
              <textarea value={request} onChange={(e) => setRequest(e.target.value)} aria-label={t("workOrder.customerRequest")} rows={3} />
              <Dictate label={t("workOrder.customerRequest")} onText={(s) => setRequest((r) => appendDictation(r, s))} />
            </Field>
            <div className="actions wide">
              <ErrorText error={error} />
              <button type="submit" className="primary" disabled={missing.length > 0 || !!photosMissing || busy}>
                {t("workOrder.create")}
              </button>
              <MissingNote missing={missing} />
              {photosMissing && <p className="missing" role="status">{t("intake.saveNeeds", { min: photosMissing.needed, done: photosMissing.done })}</p>}
              {uploading !== null && uploading > 0 && <p className="info" role="status">{t("photos.uploading", { count: String(uploading) })}</p>}
            </div>
          </div>
        </form>
      )}
    </section>
  );
}

/** Name and phone are enough to start; the rest can be filled in later on the customer page. */
function QuickCustomer({ onDone }: { onDone: (c?: Customer) => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [reminderChannels, setReminderChannels] = useState<ReminderChannel[]>([]);
  const [error, setError] = useState<unknown>(null);
  const missing = missingRequired("customer", { name, mobile }, workshop.requiredFields);
  const save = async () => {
    setError(null);
    try {
      onDone(await store.saveCustomer(workshop.id, validateCustomer({ name, mobile, reminderChannels })));
    } catch (err) {
      setError(err);
    }
  };
  return (
    <div className="form-grid">
      <TextInput label={t("customer.name")} value={name} onChange={setName} required autoFocus />
      <TextInput label={t("customer.mobile")} value={mobile} onChange={setMobile} type="tel" />
      <CustomerReminderConsent form={{ name, mobile, reminderChannels }} onChange={setReminderChannels} />
      <div className="actions wide">
        <ErrorText error={error} />
        <button type="button" className="primary" disabled={missing.length > 0 || reminderContactMissing({ name, mobile, reminderChannels })} onClick={() => void save()}>
          {t("common.save")}
        </button>
        <button type="button" onClick={() => onDone()}>
          {t("common.cancel")}
        </button>
        <MissingNote missing={missing} />
      </div>
    </div>
  );
}

function WorkOrderView({ id }: { id: string }) {
  const { t } = useTranslation();
  const { store, workshop, go, back, role } = useApp();
  const money = useMoney();
  const date = useDate();
  const [detail, setDetail] = useState<WorkOrderDetail | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [reopening, setReopening] = useState(false);
  // an invoiced work order is not reopened (a cancelling invoice comes first)
  const [invoiced, setInvoiced] = useState(false);
  const onInvoice = useCallback((inv: Invoice | null) => setInvoiced(!!inv), []);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState<WorkOrderItem | null>(null);

  const load = useCallback(async () => setDetail(await store.getWorkOrder(id)), [store, id]);
  useEffect(() => void load(), [load]);
  // a customer's answer arriving while the work order is open shows at once
  useEffect(() => {
    const on = () => void load();
    globalThis.addEventListener?.(QUOTE_ANSWERED_EVENT, on);
    return () => globalThis.removeEventListener?.(QUOTE_ANSWERED_EVENT, on);
  }, [load]);

  // The print sheet is rendered only while printing (button or the browser's own print).
  // which document: the browser's own print gives the work order's default (intake receipt or closed sheet)
  const [printing, setPrinting] = useState<PrintDoc | "auto" | null>(null);
  // damage photos of this work order: the intake receipt says how many were taken
  const [damagePhotos, setDamagePhotos] = useState(0);
  const onPhotos = useCallback((list: Photo[]) => setDamagePhotos(list.filter((p) => p.kind === "damage").length), []);
  // intake photos, phone signatures and the customer's link (features "intakePhotos" and "phoneSignature")
  const [intakePhotos, setIntakePhotos] = useState<Photo[]>([]);
  // the intake photos are read before the signature pad may open (the signed content lists them)
  const [intakeLoaded, setIntakeLoaded] = useState(false);
  const loadIntake = useCallback(async () => {
    setIntakePhotos((await store.listPhotos({ workOrderId: id })).filter((p) => p.kind === "intake"));
    setIntakeLoaded(true);
  }, [store, id]);
  useEffect(() => void loadIntake(), [loadIntake]);
  const [signatures, setSignatures] = useState<WorkOrderSignature[]>([]);
  const loadSignatures = useCallback(async () => setSignatures(await store.signatures(id)), [store, id]);
  useEffect(() => void loadSignatures(), [loadSignatures]);
  const [lastLink, setLastLink] = useState<IntakeLink | null>(null);
  useEffect(() => void store.lastIntakeLink(id).then(setLastLink, () => setLastLink(null)), [store, id, intakePhotos]);
  const [auto] = useState(() => takeAutoSign(id));
  const signOn = useFeature("phoneSignature");
  // the workshop's logo is read in advance, so the print sheet has it at once
  useWorkshopLogo();
  // closing: plainly, or with an invoice (then the payment method is chosen)
  const [withInvoice, setWithInvoice] = useState(false);
  const [payMethod, setPayMethod] = useState<PaymentMethod | null>(null);
  const [invoiceKey, setInvoiceKey] = useState(false);
  useEffect(() => {
    void store.invoiceKeySet(workshop.id).then(setInvoiceKey);
  }, [store, workshop.id]);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const vehicleId = detail?.order.vehicleId;
  const customerId = detail?.order.customerId;
  useEffect(() => {
    if (vehicleId) store.getVehicle(vehicleId).then(setVehicle, () => setVehicle(null));
  }, [store, vehicleId]);
  // the next due dates offered at closing (reminders feature); saved to the vehicle on closing
  const [dates, setDates] = useState<ReminderDates>(() => reminderDatesOf(null));
  useEffect(() => setDates(reminderDatesOf(vehicle)), [vehicle]);
  const remindersOn = useFeature("reminders");
  useEffect(() => {
    if (customerId) store.getCustomer(customerId).then(setCustomer, () => setCustomer(null));
  }, [store, customerId]);
  useEffect(() => {
    const before = () => flushSync(() => setPrinting((p) => p ?? "auto"));
    const after = () => setPrinting(null);
    globalThis.addEventListener?.("beforeprint", before);
    globalThis.addEventListener?.("afterprint", after);
    return () => {
      globalThis.removeEventListener?.("beforeprint", before);
      globalThis.removeEventListener?.("afterprint", after);
    };
  }, []);
  const print = (doc: PrintDoc | "auto" = "auto") => {
    flushSync(() => setPrinting(doc));
    globalThis.print?.();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    }
    await load();
  };

  if (detail === undefined) return <p>{t("common.loading")}</p>;
  if (detail === null) return <p className="muted">{t("common.noResults")}</p>;
  const { order, items, events, quotes } = detail;
  const closed = order.status === "closed";
  const totals = sumItems(countedLines(items));
  const pending = items.filter((i) => i.approval === "pending");
  const warranty = warrantyOf(order, items, customer, workshop);
  const shots = shotsOf(intakePhotos);
  const handover = signatures.find((x) => x.kind === "handover");

  const buyerMissing = missingBuyerData({ name: customer?.name, postalCode: customer?.postalCode, city: customer?.city, address: customer?.addressLine });
  const closeWork = (payment?: PaymentMethod) => {
    if (items.length === 0 && !globalThis.confirm?.(t("workOrder.closeEmpty"))) return;
    if (!globalThis.confirm?.(t("workOrder.closeConfirm"))) return;
    void run(async () => {
      if (remindersOn && vehicle && reminderDatesChanged(vehicle, dates)) await store.saveVehicleDates(vehicle.id, dates);
      await store.setWorkOrderStatus(order.id, "closed", payment);
      setWithInvoice(false);
      setPayMethod(null);
      // the work order is closed even if the invoice fails; it can be issued again from there
      if (payment) await store.issueInvoice(order.id);
    });
  };


  return (
    <section className="wo">
      <div className="page-head no-print">
        <button type="button" className="link" onClick={() => back("workOrders")}>
          ← {t("common.back")}
        </button>
        <h1>
          {order.number} <StatusChip status={order.status} />
        </h1>
        <button type="button" className="print-btn" onClick={() => print()}>
          <Icon name="print" size={26} />
          {t("workOrder.print")}
        </button>
      </div>


      <WorkOrderSummary order={order} items={items} quotes={quotes} />

      {order.needsReview && <p className="warn no-print">{t("workOrder.needsReviewText", { reason: order.needsReview === "plate" ? t("workOrder.reviewPlate") : order.needsReview })}</p>}

      <div className="wo-head">
        <a
          className="owner-band vehicle-band"
          href={`#vehicles/${order.vehicleId}`}
          onClick={(e) => {
            e.preventDefault();
            go("vehicles", order.vehicleId!);
          }}
        >
          <span>
            <small>{t("workOrder.vehicle")}</small>
            <strong>
              <Plate plate={order.plateNumber ?? ""} country={order.plateCountry} /> {order.vehicleLabel}
            </strong>
          </span>
          <span className="owner-go no-print">→</span>
        </a>
        <a
          className="owner-band"
          href={`#customers/${order.customerId}`}
          onClick={(e) => {
            e.preventDefault();
            go("customers", order.customerId!);
          }}
        >
          <span>
            <small>{t("workOrder.customer")}</small>
            <strong>{order.customerName}</strong>
          </span>
          <span className="owner-go no-print">→</span>
        </a>
      </div>

      <HeaderForm key={`${order.id}-${order.status}`} order={order} closed={closed} onSaved={load} vehicle={vehicle} items={items} />

      <IntakePanel
        order={order}
        customer={customer}
        photos={intakePhotos}
        onPhotosChanged={loadIntake}
        signatures={signatures}
        onSignaturesChanged={() => void loadSignatures()}
        autoSign={auto.sign && !closed && intakeLoaded}
        sendAfterSign={auto.send}
        onLink={setLastLink}
      />

      <Photos vehicleId={order.vehicleId ?? null} workOrderId={order.id} readOnly={closed} defaultKind="damage" onChange={onPhotos} />

      {!closed && order.vehicleId && <Recommendations vehicleId={order.vehicleId} order={order} onAdded={load} />}

      <h2>{t("workOrder.items")}</h2>
      {items.length === 0 ? (
        <p className="muted">{t("workOrder.noItems")}</p>
      ) : (
        <div className="table-scroll">
          <table className="list items">
            <thead>
              <tr>
                <th>{t("workOrder.item.description")}</th>
                <th>{t("workOrder.item.partNumber")}</th>
                <th className="num">{t("workOrder.item.quantity")}</th>
                <th className="num">{t("workOrder.item.unitPrice")}</th>
                <th className="num">{t("workOrder.item.vatRate")}</th>
                <th className="num">{t("workOrder.net")}</th>
                <th className="num">{t("workOrder.gross")}</th>
                {!closed && <th className="no-print" />}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className={[editing?.id === i.id && "editing", i.approval === "declined" && "declined"].filter(Boolean).join(" ") || undefined}>
                  <td>
                    <span className={`kind kind-${i.kind}`}>{t(`workOrder.kind.${i.kind}`)}</span> {i.description}
                    {i.optional && <span className="tag-optional"> {t("quote.optional")}</span>}
                    {i.customerSupplied && <span className="tag-supplied"> ({t("workOrder.item.customerSuppliedShort")})</span>}
                    {i.approval && i.approval !== "none" && <ApprovalBadge approval={i.approval} />}
                  </td>
                  <td data-label={t("workOrder.item.partNumber")} className={i.partNumber ? "part-no" : "empty"}>{i.partNumber}</td>
                  <td className="num" data-label={t("workOrder.item.quantity")}>
                    {i.quantity.toLocaleString()} {i.unit}
                  </td>
                  <td className="num" data-label={t("workOrder.item.unitPrice")}>
                    <span>
                      {money(i.unitPrice)} <span className="muted small">{t(`workOrder.priceMode.${i.priceMode}Short`)}</span>
                    </span>
                  </td>
                  <td className="num" data-label={t("workOrder.item.vatRate")}>{i.vatRate}%</td>
                  <td className="num" data-label={t("workOrder.net")}>{money(i.net)}</td>
                  <td className="num gross" data-label={t("workOrder.gross")}>{money(i.gross)}</td>
                  {!closed && (
                    <td className="no-print row-actions">
                      <button type="button" onClick={() => setEditing(i)} aria-label={`${t("workOrder.item.edit")}: ${i.description}`}>
                        {t("workOrder.item.edit")}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        aria-label={`${t("common.delete")}: ${i.description}`}
                        onClick={() => {
                          if (globalThis.confirm?.(t("common.confirmDelete"))) void run(() => store.deleteWorkOrderItem(i.id));
                        }}
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="totals">
        {totals.byVatRate.length > 1 &&
          totals.byVatRate.map((r) => (
            <div key={r.vatRate} className="muted">
              {t("workOrder.vatLine", { rate: String(r.vatRate), net: money(r.net), vat: money(r.vat) })}
            </div>
          ))}
        <div>
          {t("workOrder.net")}: <strong>{money(totals.net)}</strong>
        </div>
        <div>
          {t("workOrder.vat")}: <strong>{money(totals.vat)}</strong>
        </div>
        <div className="grand">
          {t("workOrder.gross")}: <strong>{money(totals.gross)}</strong>
        </div>
      </div>

      {!closed && (
        <ItemForm
          key={editing?.id ?? "new"}
          orderId={order.id}
          editing={editing}
          onDone={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {/* the quote box under the new line form (owner, 2026-11-01) */}
      {!closed && <QuotePanel order={order} pending={pending} quotes={quotes} customer={customer} onChanged={load} onPrint={() => print("quote")} />}
      <DeclinedWorkEmail order={order} items={items} customer={customer} vehicle={vehicle} />

      <ErrorText error={error} />

      <div className="actions wo-actions no-print">
        {nextStatuses(order.status).includes("ready") && (
          <button type="button" className="status-btn ready" onClick={() => void run(() => store.setWorkOrderStatus(order.id, "ready"))}>
            {t("workOrder.markReady")}
          </button>
        )}
        {nextStatuses(order.status).includes("open") && (
          <button type="button" onClick={() => void run(() => store.setWorkOrderStatus(order.id, "open"))}>
            {t("workOrder.markOpen")}
          </button>
        )}
        {nextStatuses(order.status).includes("closed") && (
          <>
            {vehicle && <CloseReminderDates vehicle={vehicle} odometerKm={order.odometerKm ?? null} value={dates} onChange={setDates} />}
            {/* lines still waiting for the customer's yes cannot be billed: ask first */}
            <button type="button" className="primary" onClick={() => closeWork()} disabled={pending.length > 0}>
              {t("workOrder.close")}
            </button>
            <button type="button" onClick={() => setWithInvoice(true)} disabled={pending.length > 0 || !invoiceKey || buyerMissing.length > 0 || withInvoice}>
              {t("close.withInvoice")}
            </button>
            {!invoiceKey && <p className="muted">{t("invoice.keyMissing")}</p>}
            {invoiceKey && buyerMissing.length > 0 && (
              <p className="missing">{t("invoice.customerMissing", { fields: buyerMissing.map((k) => t(k === "name" ? "customer.name" : `customer.${k}`)).join(", ") })}</p>
            )}
            {withInvoice && (
              <div className="close-invoice">
                <PaymentChoice value={payMethod} onChange={setPayMethod} />
                <div className="actions">
                  <button type="button" className="primary" disabled={!payMethod} onClick={() => payMethod && closeWork(payMethod)}>
                    {t("close.closeAndInvoice")}
                  </button>
                  <button type="button" onClick={() => { setWithInvoice(false); setPayMethod(null); }}>{t("common.cancel")}</button>
                  {!payMethod && <span className="missing">{t("payment.choose")}</span>}
                </div>
              </div>
            )}
            {pending.length > 0 && <p className="missing">{t("quote.closePending", { count: pending.length })}</p>}
          </>
        )}
        {!closed && can(role, workshop, "workOrder.delete") && (
          <button
            type="button"
            className="danger"
            onClick={() => {
              if (globalThis.confirm?.(t("common.confirmDelete"))) void run(async () => { await store.deleteWorkOrder(order.id); go("workOrders"); });
            }}
          >
            {t("workOrder.delete")}
          </button>
        )}
      </div>

      {closed && (
        <div className="closed-band no-print">
          <p>
            <strong>{t("workOrder.closedTitle", { date: date(order.closedAt) })}</strong> {t("workOrder.closedText")}
          </p>
          <PaymentInvoice order={order} customer={customer} onChanged={load} onInvoice={onInvoice} />
          {handover ? (
            <HandoverNote sig={handover} order={order} customer={customer} shots={shots} />
          ) : (
            signOn && (
              <SignButton kind="handover" order={order} customer={customer} shots={shots} label={t("signature.handoverButton")} onSigned={() => void loadSignatures()} />
            )
          )}
          {warranty.applies && (
            <button type="button" className="print-btn" onClick={() => print("warranty")}>
              <Icon name="warranty" size={26} />
              {t("workOrder.printWarranty")}
            </button>
          )}
          {can(role, workshop, "workOrder.reopen") && invoiced && <p className="muted">{t("workOrder.reopenInvoiced")}</p>}
          {can(role, workshop, "workOrder.reopen") && !invoiced &&
            (reopening ? (
              <div className="actions">
                <TextInput label={t("workOrder.reopenReason")} value={reason} onChange={setReason} help="workOrder.reopen" />
                <button
                  type="button"
                  className="primary"
                  disabled={!reason.trim()}
                  onClick={() => void run(async () => { await store.reopenWorkOrder(order.id, reason); setReopening(false); setReason(""); })}
                >
                  {t("workOrder.reopen")}
                </button>
                <button type="button" onClick={() => setReopening(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setReopening(true)}>
                {t("workOrder.reopen")}
              </button>
            ))}
        </div>
      )}

      {printing && (
        <PrintSheet
          doc={printing === "auto" ? (closed ? "closed" : "intake") : printing}
          order={order}
          items={items}
          quotes={quotes}
          vehicle={vehicle}
          customer={customer}
          damagePhotos={damagePhotos}
          shots={shots}
          signatures={signatures}
          link={lastLink}
        />
      )}

      <details className="history no-print">
        <summary>{t("workOrder.history")}</summary>
        <ul>
          {events.map((e, n) => (
            <li key={n}>
              {date(e.at)} · {t(`workOrder.event.${e.kind}`)}
              {e.reason && <> · {e.kind === "quote_answered" ? t(`quote.via.${e.reason}`) : e.reason}</>}
            </li>
          ))}
        </ul>
      </details>

      {/* the type data sheet at the bottom of an open work order (owner's request, 2026-11-01) */}
      {!closed && vehicle && (
        <div className="no-print">
          <VehicleDataSheet
            key={`${vehicle.id}-${vehicle.typeId ?? ""}`}
            vehicle={vehicle}
            onChanged={() => void store.getVehicle(vehicle.id).then((v) => v && setVehicle(v))}
          />
        </div>
      )}
    </section>
  );
}

function HeaderForm({ order, closed, onSaved, vehicle, items }: { order: WorkOrder; closed: boolean; onSaved: () => Promise<void>; vehicle: Vehicle | null; items: WorkOrderItem[] }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const quotesOn = useFeature("quotes");
  const [km, setKm] = useState(order.odometerKm === null ? "" : String(order.odometerKm));
  const [request, setRequest] = useState(order.customerRequest ?? "");
  const [findings, setFindings] = useState(order.findings ?? "");
  const [readyAt, setReadyAt] = useState(order.expectedReadyAt ?? "");
  const [notes, setNotes] = useState(order.notes ?? "");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  // The version of the work order this form was filled from; saving checks it so another tab's newer save is not overwritten.
  const [base, setBase] = useState(order.updatedAt);
  const kmValue = parseNumber(km);
  const input = {
    vehicleId: order.vehicleId,
    customerId: order.customerId,
    odometerKm: odometerFromInput(kmValue),
    expectedReadyAt: readyAt || null,
    customerRequest: request,
    findings,
    notes,
  };
  // Imported work orders have no odometer reading; they are closed and read only.
  const missing = order.legacyId ? [] : missingRequired("workOrder", input, workshop.requiredFields);
  const dirty =
    input.odometerKm !== order.odometerKm ||
    readyAt !== (order.expectedReadyAt ?? "") ||
    request !== (order.customerRequest ?? "") ||
    findings !== (order.findings ?? "") ||
    notes !== (order.notes ?? "");
  // A reload that brought newer data while nothing was typed here: the form shows the same values, so take its version.
  useEffect(() => {
    if (!dirty) setBase(order.updatedAt);
  }, [order.updatedAt]);
  const date = useDate();
  const readyRequired = useRequired("workOrder", input, true).isReq("expectedReadyAt");

  if (closed) {
    return (
      <dl className="wo-facts">
        <div>
          <dt>{t("workOrder.odometerKm")}</dt>
          <dd>{order.odometerKm ?? "–"}</dd>
        </div>
        {order.expectedReadyAt && (
          <div>
            <dt>{t("workOrder.expectedReadyAt")}</dt>
            <dd>{date(order.expectedReadyAt)}</dd>
          </div>
        )}
        {order.customerRequest && (
          <div className="wide">
            <dt>{t("workOrder.customerRequest")}</dt>
            <dd className="pre">{order.customerRequest}</dd>
          </div>
        )}
        {order.findings && (
          <div className="wide">
            <dt>{t("workOrder.findings")}</dt>
            <dd className="pre">{order.findings}</dd>
          </div>
        )}
        {order.notes && (
          <div className="wide">
            <dt>{t("workOrder.notes")}</dt>
            <dd className="pre">{order.notes}</dd>
          </div>
        )}
      </dl>
    );
  }

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const next = await store.updateWorkOrder(order.id, input, base);
      setBase(next);
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="form-grid" onSubmit={save} noValidate>
      <TextInput label={t("workOrder.odometerKm")} value={km} onChange={(v) => { setSaved(false); setKm(v); }} required invalid={!input.odometerKm && input.odometerKm !== 0} help="workOrder.odometerKm" />
      <DueServices
        vehicle={vehicle}
        km={input.odometerKm}
        items={items}
        onAdd={async (lines) => {
          for (const line of lines) await store.saveWorkOrderItem(workshop.id, order.id, line);
          await onSaved();
        }}
      />
      <TextInput
        label={t("workOrder.expectedReadyAt")}
        value={readyAt}
        onChange={(v) => { setSaved(false); setReadyAt(v); }}
        type="date"
        invalid={missing.some((d) => d.field === "expectedReadyAt")}
        required={readyRequired}
        help="workOrder.expectedReadyAt"
      />
      <Field label={t("workOrder.customerRequest")} wide help="workOrder.customerRequest">
        <textarea value={request} onChange={(e) => { setSaved(false); setRequest(e.target.value); }} aria-label={t("workOrder.customerRequest")} rows={3} />
        <Dictate label={t("workOrder.customerRequest")} onText={(s) => { setSaved(false); setRequest((r) => appendDictation(r, s)); }} />
      </Field>
      <Field label={quotesOn ? `${t("workOrder.findings")} (${t("workOrder.findingsSent")})` : t("workOrder.findings")} wide help="workOrder.findings">
        <textarea value={findings} onChange={(e) => { setSaved(false); setFindings(e.target.value); }} aria-label={t("workOrder.findings")} rows={3} />
        <Dictate label={t("workOrder.findings")} onText={(s) => { setSaved(false); setFindings((r) => appendDictation(r, s)); }} />
      </Field>
      <Field label={t("workOrder.notes")} wide>
        <textarea value={notes} onChange={(e) => { setSaved(false); setNotes(e.target.value); }} aria-label={t("workOrder.notes")} rows={2} />
        <Dictate label={t("workOrder.notes")} onText={(s) => { setSaved(false); setNotes((r) => appendDictation(r, s)); }} />
      </Field>
      <div className="actions wide no-print">
        <ErrorText error={error} />
        {saved && <span className="ok">{t("common.saved")}</span>}
        <button type="submit" disabled={!dirty || missing.length > 0}>
          {t("common.save")}
        </button>
        <MissingNote missing={missing} />
      </div>
    </form>
  );
}

const KINDS: ItemKind[] = ["part", "labour", "other"];

function ItemForm({ orderId, editing, onDone }: { orderId: string; editing: WorkOrderItem | null; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const cur = currency(workshop.currency);
  const defaultVat = Number(workshop.general.defaultVatRate ?? 27);
  const hourly = workshop.general.hourlyRateNet;
  const [kind, setKind] = useState<ItemKind>(editing?.kind ?? "part");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [partNumber, setPartNumber] = useState(editing?.partNumber ?? "");
  const [quantity, setQuantity] = useState(editing ? String(editing.quantity) : "1");
  const [unit, setUnit] = useState(editing?.unit ?? t("workOrder.unit.piece"));
  const [price, setPrice] = useState(editing ? String(fromMinor(editing.unitPrice, workshop.currency)) : "");
  const [priceMode, setPriceMode] = useState<PriceMode>(editing?.priceMode ?? "gross");
  const [vatRate, setVatRate] = useState(editing?.vatRate ?? defaultVat);
  const [suggestions, setSuggestions] = useState<ItemSuggestion[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [article, setArticle] = useState<Article | null>(null);
  const [error, setError] = useState<unknown>(null);
  const tracking = useStockTracking();
  const quotesOn = useFeature("quotes");
  const [needsApproval, setNeedsApproval] = useState(!!editing && !!editing.approval && editing.approval !== "none");
  const [optional, setOptional] = useState(editing?.optional ?? false);
  const [customerSupplied, setCustomerSupplied] = useState(editing?.customerSupplied ?? false);
  // a line already answered keeps its answer (a changed price asks again by itself)
  const approval = !needsApproval ? "none" : editing?.approval && editing.approval !== "none" ? editing.approval : "pending";

  // The article of a line being edited (for the stock warning).
  useEffect(() => {
    if (editing?.articleId) void store.getArticle(editing.articleId).then(setArticle);
  }, [editing?.articleId, store]);

  // Typing the description offers articles and lines used before.
  useEffect(() => {
    if (editing || article || description.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const h = setTimeout(() => {
      void store.itemSuggestions(workshop.id, description).then(setSuggestions);
      if (kind !== "labour") void store.searchArticles(workshop.id, description, 6).then(setArticles);
    }, 250);
    return () => clearTimeout(h);
  }, [description, editing, article, kind, store, workshop.id]);
  useEffect(() => {
    if (article || (description.trim().length < 2 && partNumber.trim().length < 3)) setArticles([]);
  }, [article, description, partNumber]);

  // Typing a part number offers the article with that number (also cross numbers).
  useEffect(() => {
    if (editing || article || kind === "labour" || partNumber.trim().length < 3) return;
    const h = setTimeout(() => void store.searchArticles(workshop.id, partNumber, 6).then(setArticles), 250);
    return () => clearTimeout(h);
  }, [partNumber, editing, article, kind, store, workshop.id]);

  const applyArticle = (a: Article) => {
    setArticle(a);
    setKind("part");
    setDescription(a.name);
    setPartNumber(a.partNumber ?? "");
    setUnit(a.unit ?? t("workOrder.unit.piece"));
    if (a.salePrice !== null && a.salePrice !== undefined) {
      setPrice(String(fromMinor(a.salePrice, workshop.currency)));
      setPriceMode(a.salePriceMode ?? "gross");
    }
    setVatRate(a.vatRate ?? defaultVat);
    setArticles([]);
    setSuggestions([]);
  };

  const pickKind = (k: ItemKind) => {
    setKind(k);
    if (k === "labour") {
      setUnit(t("workOrder.unit.hour"));
      if (!price && typeof hourly === "number") {
        setPrice(String(fromMinor(hourly, workshop.currency)));
        setPriceMode("net");
      }
    } else if (unit === t("workOrder.unit.hour")) setUnit(t("workOrder.unit.piece"));
  };

  const applySuggestion = (s: ItemSuggestion) => {
    setKind(s.kind);
    setDescription(s.description);
    setPartNumber(s.partNumber ?? "");
    setUnit(s.unit ?? "");
    setPrice(String(fromMinor(s.unitPrice, workshop.currency)));
    setPriceMode(s.priceMode);
    setVatRate(s.vatRate);
    setSuggestions([]);
  };

  const q = parseNumber(quantity);
  const p = parseNumber(price);
  const line: WorkOrderLineInput | null =
    q !== null && !Number.isNaN(q) && q > 0 && p !== null && !Number.isNaN(p) && p >= 0 && description.trim()
      ? { kind, description, partNumber, articleId: kind === "part" && customerSupplied ? null : article?.id ?? editing?.articleId ?? null, unit, quantity: q, unitPrice: toMinor(p, workshop.currency), priceMode, vatRate,
          approval, optional: needsApproval && optional, customerSupplied: kind === "part" && customerSupplied }
      : null;
  let preview: { net: number; gross: number } | null = null;
  try {
    preview = line ? buildLine(line) : null;
  } catch {
    preview = null;
  }
  const missingLabels = [
    !description.trim() && t("workOrder.item.description"),
    (q === null || Number.isNaN(q) || q <= 0) && t("workOrder.item.quantity"),
    p === null && t("workOrder.item.unitPrice"),
  ].filter(Boolean) as string[];
  // a typed price that is not a number or is negative (#129): red, the reason under the grey button
  const priceBad = p !== null && (Number.isNaN(p) || p < 0);

  const money = useMoney();

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!line) return setError(new DomainError("REQUIRED_MISSING"));
    try {
      await store.saveWorkOrderItem(workshop.id, orderId, line, editing?.id);
      setDescription("");
      setPartNumber("");
      setQuantity("1");
      setPrice("");
      setArticle(null);
      setOptional(false);
      setCustomerSupplied(false);
      setNeedsApproval(false);
      await onDone();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="item-form form-grid no-print" onSubmit={save} noValidate>
      <h3 className="wide">{t(editing ? "workOrder.item.editTitle" : "workOrder.item.addTitle")}</h3>
      <div className="segmented wide" role="group" aria-label={t("workOrder.item.kind")}>
        {KINDS.map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => pickKind(k)}>
            {t(`workOrder.kind.${k}`)}
          </button>
        ))}
      </div>
      <Field label={t("workOrder.item.description")} wide required invalid={false} help="workOrder.item.description">
        <div className="owner">
          <input value={description} onChange={(e) => setDescription(e.target.value)} aria-label={t("workOrder.item.description")} />
          <Dictate label={t("workOrder.item.description")} onText={(s) => setDescription((d) => appendDictation(d, s))} />
          {articles.length > 0 && (
            <ul className="suggest articles" aria-label={t("workOrder.item.fromArticles")}>
              {articles.map((a) => (
                <li key={a.id}>
                  <button type="button" onClick={() => applyArticle(a)}>
                    <span className="art-name">{a.name}</span>
                    {a.partNumber && <span className="part-no sub">{a.partNumber}</span>}
                    {a.salePrice !== null && a.salePrice !== undefined && <span className="muted"> · {money(a.salePrice)} {t(`workOrder.priceMode.${a.salePriceMode ?? "gross"}Short`)}</span>}
                    {tracking && <> <StockBadge article={a} /></>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions.length > 0 && (
            <ul className="suggest">
              {suggestions.map((s) => (
                <li key={s.description}>
                  <button type="button" onClick={() => applySuggestion(s)}>
                    {s.description} <span className="muted">· {money(s.unitPrice)} {t(`workOrder.priceMode.${s.priceMode}Short`)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Field>
      {kind !== "labour" && (
        <TextInput
          label={t("workOrder.item.partNumber")}
          value={partNumber}
          onChange={(v) => {
            setPartNumber(v);
            // a different number is no longer that article
            if (article && v.trim().toUpperCase() !== (article.partNumber ?? "")) setArticle(null);
          }}
        />
      )}
      <TextInput label={t("workOrder.item.quantity")} value={quantity} onChange={setQuantity} required invalid={quantity.trim() !== "" && !(q !== null && q > 0)} />
      {article && tracking && article.trackStock && (
        <p className={`wide stock-note stock-note-${stockStatus(article, (q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0))}`}>
          <StockBadge article={article} needed={(q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0)} />{" "}
          {stockStatus(article, (q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0)) === "out" && t("stock.notEnough")}
        </p>
      )}
      <TextInput label={t("workOrder.item.unit")} value={unit} onChange={setUnit} />
      <TextInput label={`${t("workOrder.item.unitPrice")} (${cur.code})`} value={price} onChange={setPrice} required invalid={priceBad} />
      <Field label={t("workOrder.item.priceMode")} help="workOrder.priceMode">
        <select value={priceMode} onChange={(e) => setPriceMode(e.target.value as PriceMode)} aria-label={t("workOrder.item.priceMode")}>
          <option value="gross">{t("workOrder.priceMode.gross")}</option>
          <option value="net">{t("workOrder.priceMode.net")}</option>
        </select>
      </Field>
      <Field label={t("workOrder.item.vatRate")}>
        <select value={vatRate} onChange={(e) => setVatRate(Number(e.target.value))} aria-label={t("workOrder.item.vatRate")}>
          {[...new Set([...HU_VAT_RATES, vatRate])].map((r) => (
            <option key={r} value={r}>
              {r}%
            </option>
          ))}
        </select>
      </Field>
      {quotesOn && (
        <div className="wide approval-switches">
          <span className="check-row">
            <label className="check">
              <input type="checkbox" checked={needsApproval} onChange={(e) => setNeedsApproval(e.target.checked)} />
              {t("quote.needsApproval")}
            </label>
            <Help id="quote.needsApproval" />
          </span>
          {needsApproval && (
            <label className="check">
              <input type="checkbox" checked={optional} onChange={(e) => setOptional(e.target.checked)} />
              {t("quote.optionalLong")}
            </label>
          )}
        </div>
      )}
      {/* rare: a part the customer brought; small and below the quote ticks (owner, 2026-11-01) */}
      {kind === "part" && (
        <label className="check tiny wide">
          <input type="checkbox" checked={customerSupplied} onChange={(e) => setCustomerSupplied(e.target.checked)} />
          {t("workOrder.item.customerSupplied")}
        </label>
      )}
      <div className="actions wide">
        {preview && (
          <span className="line-preview">
            {t("workOrder.net")}: <strong>{money(preview.net)}</strong> · {t("workOrder.gross")}: <strong>{money(preview.gross)}</strong>
          </span>
        )}
        <ErrorText error={error} />
        <button type="submit" className="primary" disabled={!line || missingLabels.length > 0 || priceBad}>
          {t(editing ? "common.save" : "workOrder.item.add")}
        </button>
        {editing && (
          <button type="button" onClick={() => void onDone()}>
            {t("common.cancel")}
          </button>
        )}
        {missingLabels.length > 0 && (description || price) && <p className="missing">{t("required.stillMissing", { fields: missingLabels.join(", ") })}</p>}
        {priceBad && <p className="missing">{t("error.INVALID_PRICE.action")}</p>}
      </div>
    </form>
  );
}

function ApprovalBadge({ approval }: { approval: NonNullable<WorkOrderItem["approval"]> }) {
  const { t } = useTranslation();
  return <span className={`approval approval-${approval}`}> {t(`quote.approval.${approval}`)}</span>;
}

/** The customer's link to the quote page. */
export const quoteLink = (token: string) => `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#quote/${token}`;

/**
 * Asking the customer about the lines marked for approval: a link to send
 * (share, e-mail or copy), or a yes recorded by phone.
 */
function QuotePanel({ order, pending, quotes, customer, onChanged, onPrint }: { order: WorkOrder; pending: WorkOrderItem[]; quotes: QuoteRequest[]; customer: Customer | null; onChanged: () => Promise<void>; onPrint: () => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const money = useMoney();
  const date = useDate();
  const on = useFeature("quotes");
  const [error, setError] = useState<unknown>(null);
  const [phone, setPhone] = useState(false);
  const [yes, setYes] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState<QuoteChannel | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const smsOn = useFeature("sms");
  // the feature off (e.g. after the Profi week of the trial) with lines still waiting: only the answer by phone,
  // so the work order can be closed; a link already sent keeps working for the customer until it expires
  if (pending.length === 0 && (!on || quotes.length === 0)) return null;
  const last = quotes[0];
  const open = last && last.channel === "link" && !last.answeredAt && last.expiresAt > new Date().toISOString() ? last : null;
  const link = open ? quoteLink(open.token) : null;
  const sum = pending.reduce((a, i) => a + i.gross, 0);
  const findings = order.findings?.trim() ?? "";

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err);
    }
  };
  const share = async () => {
    if (!link) return;
    const shareText = t("quote.shareText", { number: order.number, plate: order.plateNumber ?? "" });
    const text = findings ? `${t("quote.findingsText", { findings })}\n\n${shareText}` : shareText;
    if (globalThis.navigator?.share) await globalThis.navigator.share({ title: t("quote.title"), text, url: link }).catch(() => undefined);
    else {
      await globalThis.navigator?.clipboard?.writeText(link);
      setCopied(true);
    }
  };
  // addressed by name like the quote page (#113), signed with the workshop's name and contact
  const customerName = customer?.name?.trim() ?? "";
  const greeting = customerName ? t("quote.page.greeting", { name: customerName }) : t("quote.page.greetingNoName");
  const contact = (key: string) => {
    const v = workshop.general[key];
    return typeof v === "string" ? v.trim() : "";
  };
  const signature = [workshop.name, contact("workshopAddress"), contact("workshopPhone"), contact("workshopEmail")].filter(Boolean).join("\n");
  const mailBody = link ? [greeting, t("quote.mailBody", { plate: order.plateNumber ?? "", link }), signature].filter(Boolean).join("\n\n") : "";
  // the discovered fault goes with the quote, right after the link
  const body = link && findings ? mailBody.replace(link, `${link}\n\n${t("quote.findingsText", { findings })}`) : mailBody;
  // short message for SMS and chat apps: text, fault, link
  const shortText = link
    ? [t("quote.shareText", { number: order.number, plate: order.plateNumber ?? "" }), findings ? t("quote.findingsText", { findings }) : "", link].filter(Boolean).join("\n")
    : "";
  // the main phone number is "mobile" (one field on the form); "phone" is the older second one
  const phoneRaw = customer?.mobile || customer?.phone || "";
  const phoneDigits = phoneRaw ? normalizePhone(phoneRaw) : null;
  const sendBy = (channel: QuoteChannel) =>
    void run(async () => {
      if (!link) return;
      setSending(channel);
      try {
        const message = channel === "email"
          ? { subject: t("quote.mailSubject", { number: order.number }), text: body, link, buttonLabel: t("quote.mailButton") }
          : { subject: "", text: shortText, link, buttonLabel: "" };
        const { to, price } = await store.sendQuote(order.id, channel, message);
        setSent(`${t(channel === "email" ? "quote.emailSent" : "quote.smsSent", { to })}${price > 0 ? ` ${t("sms.priceNote", { price: money(price) })}` : ""}`);
      } finally {
        setSending(null);
      }
    });

  return (
    <section className="quote-panel no-print" aria-label={t("quote.title")}>
      <h3>
        {t("quote.title")}
        <Help id="quote.panel" /> <PlanTag featureKey="quotes" />
      </h3>
      {!on && <p className="warn">{t("quote.offNote")}</p>}
      {pending.length > 0 && <p className="warn">{t("quote.pendingSummary", { count: pending.length, sum: money(sum) })}</p>}
      {pending.length > 0 && <p className="muted">{order.findings?.trim() ? t("quote.findingsShown") : t("quote.findingsEmpty")}</p>}
      {last?.answeredAt && (
        <p className="ok-band">
          {t("quote.answered", { date: date(last.answeredAt), via: t(`quote.via.${last.channel}`), count: last.approvedIds.length, total: last.itemIds.length })}
        </p>
      )}
      {open && <p className="muted">{t("quote.waiting", { date: date(open.createdAt), until: date(open.expiresAt) })}</p>}
      <div className="actions">
        <button type="button" className="print-btn" onClick={onPrint}>
          <Icon name="quote" size={26} />
          {t("workOrder.printQuote")}
        </button>
      </div>
      <ErrorText error={error} />
      {pending.length > 0 && !phone && (
        <div className="actions">
          {!on ? null : link ? (
            <>
              <button type="button" className="primary" onClick={() => void share()}>
                {t("quote.share")}
              </button>
              {customer?.email && (
                <button type="button" onClick={() => sendBy("email")} disabled={sending !== null}>
                  {sending === "email" ? t("quote.sending") : t("quote.email")}
                </button>
              )}
              {smsOn && phoneDigits && (
                <button type="button" onClick={() => sendBy("sms")} disabled={sending !== null}>
                  {sending === "sms" ? t("quote.sending") : t("quote.smsSend")}
                </button>
              )}
              {phoneDigits && (
                <a className="button" href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(shortText)}`} target="_blank" rel="noreferrer">
                  {t("quote.whatsapp")}
                </a>
              )}
              <a className="button" href={`viber://forward?text=${encodeURIComponent(shortText)}`}>
                {t("quote.viber")}
              </a>
              {!smsOn && phoneDigits && (
                <a className="button" href={`sms:+${phoneDigits}?body=${encodeURIComponent(shortText)}`}>
                  {t("quote.smsApp")}
                </a>
              )}
              <input className="quote-link" readOnly value={link} aria-label={t("quote.link")} onFocus={(e) => e.target.select()} />
              {copied && <span className="ok">{t("quote.copied")}</span>}
              {sent && <span className="ok">{sent}</span>}
            </>
          ) : (
            <button type="button" className="primary" onClick={() => void run(() => store.createQuoteRequest(order.id))}>
              {t("quote.send")}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setYes(pending.map((i) => i.id));
              setPhone(true);
            }}
          >
            {t("quote.byPhone")}
          </button>
        </div>
      )}
      {phone && (
        <div className="phone-answer">
          <p>{t("quote.byPhoneIntro")}</p>
          {pending.map((i) => (
            <label key={i.id} className="check">
              <input type="checkbox" checked={yes.includes(i.id)} onChange={(e) => setYes((y) => (e.target.checked ? [...y, i.id] : y.filter((x) => x !== i.id)))} />
              {i.description} · {money(i.gross)}
            </label>
          ))}
          <TextInput label={t("quote.note")} value={note} onChange={setNote} />
          <div className="actions">
            <button
              type="button"
              className="primary"
              onClick={() =>
                void run(async () => {
                  await store.recordQuoteAnswer(order.id, yes, note);
                  setPhone(false);
                  setNote("");
                })
              }
            >
              {t("quote.saveAnswer")}
            </button>
            <button type="button" onClick={() => setPhone(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** The printed documents of a work order (owner, 2026-11-03: the four sheets of "A · Munkalap"). */
export type PrintDoc = "intake" | "closed" | "warranty" | "quote";

/** A text setting of the workshop, trimmed, or null. */
function generalText(workshop: { general: Record<string, unknown> }, k: string) {
  const v = workshop.general[k];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/**
 * Whether a closed work order carries a warranty, and its facts: the statutory one (a consumer above
 * the threshold of the legal profile) or the workshop's own terms. Same condition on the closed sheet,
 * on the warranty card and for its print button.
 */
export function warrantyOf(order: WorkOrder, items: WorkOrderItem[], customer: Customer | null, workshop: { general: Record<string, unknown>; currency: string }) {
  const profile = legalProfile(generalText(workshop, "legalCountry") ?? "HU");
  const own = generalText(workshop, "warrantyText");
  const closed = order.status === "closed";
  const gross = sumItems(countedLines(items)).gross;
  // a work order without a company customer is treated as a consumer's (the safe side)
  const statutory = closed && warrantyRequired(profile, { consumer: !customer?.isCompany, grossMinor: gross, currency: workshop.currency });
  const handover = order.closedAt ?? order.readyAt;
  const months = statutory && profile.consumerWarranty ? profile.consumerWarranty.months : null;
  const until = months && handover ? warrantyUntil(handover, months) : null;
  return { profile, own, statutory, handover, months, until, applies: closed && (statutory || !!own) };
}

/** The latest quote link that still works (its QR code and address go on the printed quote). */
function openQuoteLink(quotes: QuoteRequest[]) {
  const now = new Date().toISOString();
  const q = quotes.find((x) => x.channel === "link" && x.expiresAt > now);
  return q ? { link: quoteLink(q.token), quote: q } : null;
}

/** Lines of the work order that were put to the customer as a quote. */
const quotedLines = (items: WorkOrderItem[]) => items.filter((i) => i.approval && i.approval !== "none");

/**
 * The printed work order: a sheet of its own (A4, normal print sizes), only visible when printing; the
 * screen view is hidden then. One component, four documents: the intake receipt (open work order), the
 * closed work order, the warranty card and the quote.
 */
function PrintSheet({ doc, order, items: allItems, quotes, vehicle, customer, damagePhotos, shots = [], signatures = [], link = null }: {
  doc: PrintDoc;
  order: WorkOrder;
  items: WorkOrderItem[];
  quotes: QuoteRequest[];
  vehicle: Vehicle | null;
  customer: Customer | null;
  damagePhotos: number;
  /** Intake photos (the collage of the intake receipt). */
  shots?: IntakeShot[];
  /** Phone signatures: the intake one on the intake receipt, the handover one on the closed sheet. */
  signatures?: WorkOrderSignature[];
  /** The customer's intake link ("+N a linken"). */
  link?: IntakeLink | null;
}) {
  const { t, i18n } = useTranslation();
  const { workshop } = useApp();
  const money = useMoney();
  const date = useDate();
  const dateTime = (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "";
  const g = (k: string) => generalText(workshop, k);
  // declined lines are not part of the work order any more (the quote shows them struck through)
  const items = doc === "quote" ? allItems : countedLines(allItems);
  const counted = countedLines(allItems);
  const totals = sumItems(counted);
  const answered = quotes.find((q) => q.answeredAt);
  const address = customer ? [customer.postalCode, customer.city, customer.addressLine].filter(Boolean).join(" ") : "";
  const phone = customer ? [customer.mobile, customer.phone].filter(Boolean).join(", ") : "";
  const vehicleName = vehicle ? [vehicle.make, vehicle.model, vehicle.typeDescription].filter(Boolean).join(" ") : order.vehicleLabel ?? "";
  const w = warrantyOf(order, allItems, customer, workshop);
  const kmText = order.odometerKm === null ? null : `${order.odometerKm.toLocaleString(i18n.language)} km`;
  const plate = order.plateNumber ? <Plate plate={order.plateNumber} country={order.plateCountry} size="sm" /> : null;
  const qr = doc === "quote" ? openQuoteLink(quotes) : null;
  // the customer's phone signature sits above the customer's signature line (paper signature otherwise)
  const phoneSig = doc === "intake" ? signatures.find((x) => x.kind === "intake") : doc === "closed" ? signatures.find((x) => x.kind === "handover") : undefined;

  const title = t(
    doc === "closed" ? "workOrder.print.closedTitle" : doc === "warranty" ? "workOrder.print.warrantyDocTitle" : doc === "quote" ? "workOrder.print.quoteTitle" : "workOrder.print.openTitle",
  );
  const meta =
    doc === "intake"
      ? `${t("workOrder.print.receivedAt")}: ${dateTime(order.receivedAt)}`
      : doc === "closed"
        ? [`${t("workOrder.print.receivedAt")}: ${date(order.receivedAt)}`, order.closedAt && `${t("workOrder.print.closedAt")}: ${date(order.closedAt)}`].filter(Boolean).join(" · ")
        : doc === "quote"
          ? [`${t("workOrder.print.issuedAt")}: ${date(new Date().toISOString())}`, qr && `${t("workOrder.print.validUntil")}: ${date(qr.quote.expiresAt)}`].filter(Boolean).join(" · ")
          : t("warranty.ofOrder", { number: order.number });

  const head = (
    <DocHead
      logo={<WorkshopLogo className="ps-logo" />}
      shop={workshop.name}
      lines={[
        // contacts on two compact lines: where and how to reach us, then the company numbers
        [g("workshopAddress"), g("workshopPhone")].filter(Boolean).join(" · "),
        [g("workshopTaxNumber") && `${t("customer.taxNumber")}: ${g("workshopTaxNumber")}`, g("workshopRegNumber") && `${t("workOrder.print.regNumber")}: ${g("workshopRegNumber")}`]
          .filter(Boolean)
          .join(" · "),
      ]}
      title={title}
      number={doc === "warranty" ? t("warranty.number", { number: order.number }) : order.number}
      meta={meta}
    />
  );
  const foot = (
    <div className="ps-foot">
      <MadeWith />
    </div>
  );

  if (doc === "warranty") {
    const covered = counted.filter((i) => !i.customerSupplied);
    const supplied = counted.filter((i) => i.customerSupplied);
    const key = w.profile.textKey;
    return (
      <div className="print-sheet ps-doc-warranty">
        {head}
        <section className="ps-badge">
          <Icon name="warranty" size={52} />
          {w.months ? (
            <div className="ps-months">
              <b>{w.months}</b>
              <small>{t("warranty.monthsLabel")}</small>
            </div>
          ) : (
            <div className="ps-months">
              <small>{t("workOrder.print.warrantyDocTitle")}</small>
            </div>
          )}
          <div className="ps-badge-facts">
            <div>
              <p className="ps-cap">{t("warranty.start")}</p>
              <b className="mono">{date(w.handover)}</b>
            </div>
            {w.until && (
              <div>
                <p className="ps-cap">{t("warranty.end")}</p>
                <b className="mono">{date(w.until)}</b>
              </div>
            )}
            {kmText && (
              <div>
                <p className="ps-cap">{t("warranty.km")}</p>
                <b className="mono">{kmText}</b>
              </div>
            )}
            {plate && (
              <div>
                <p className="ps-cap">{t("workOrder.vehicle")}</p>
                {plate}
              </div>
            )}
          </div>
        </section>
        {w.own && (
          <section className="ps-box">
            <p className="ps-cap">{t("warranty.terms")}</p>
            <p className="pre">{w.own}</p>
          </section>
        )}
        <section className="ps-box">
          <p className="ps-cap">{t("warranty.items")}</p>
          <table className="ps-items">
            <thead>
              <tr>
                <th>{t("workOrder.item.description")}</th>
                <th>{t("workOrder.item.partNumber")}</th>
                <th className="num">{t("workOrder.item.quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {covered.map((i) => (
                <tr key={i.id}>
                  <td>{i.description}</td>
                  <td className="mono">{i.partNumber}</td>
                  <td className="num">
                    {i.quantity.toLocaleString(i18n.language)} {i.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {supplied.length > 0 && <p className="ps-note">{t("warranty.suppliedNote", { items: supplied.map((i) => i.description).join(", ") })}</p>}
        </section>
        <section className="ps-box">
          <p className="ps-cap">{t("warranty.whatToDo")}</p>
          <p>{t(`legal.${key}.warrantySteps`)}</p>
        </section>
        <p className="ps-legal">{t(`legal.${key}.warrantyLaw`)}</p>
        <Signatures left={t("warranty.signWorkshop")} right={t("warranty.signCustomer")} />
        <div className="ps-cut">
          <span>{t("warranty.claimTitle")}</span>
        </div>
        <section className="ps-claim">
          <div>
            <p className="ps-cap">{t("warranty.claimNumber")}</p>
            <div className="mono ps-claim-no">{t("warranty.number", { number: order.number })}</div>
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimDate")}</p>
            <div className="ps-line" />
          </div>
          <div className="ps-wide">
            <p className="ps-cap">{t("warranty.claimFault")}</p>
            <div className="ps-line" />
            <div className="ps-line" />
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimRemedy")}</p>
            <div className="ps-checks">
              {(["repair", "redo", "discount", "withdraw"] as const).map((r) => (
                <span key={r}>
                  <i aria-hidden="true" />
                  {t(`warranty.remedy.${r}`)}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimDecision")}</p>
            <div className="ps-line" />
            <div className="ps-line" />
          </div>
        </section>
        {foot}
      </div>
    );
  }

  const quoted = doc === "quote" ? quotedLines(allItems) : [];
  const grandLabel = t(doc === "closed" ? "workOrder.print.toPay" : "workOrder.print.expectedTotal");
  return (
    <div className={`print-sheet ps-doc-${doc}`}>
      {head}

      <div className="ps-boxes">
        <section>
          <p className="ps-cap">{t("workOrder.vehicle")}</p>
          <KeyValues
            rows={[
              [t("vehicle.plateNumber"), plate],
              [t("vehicle.model"), vehicleName],
              [t("vehicle.vin"), doc === "quote" ? null : vehicle?.vin, "mono"],
              [t("vehicle.engineCode"), doc === "closed" ? vehicle?.engineCode : null, "mono"],
              [t("workOrder.odometerKm"), kmText, "mono"],
            ]}
          />
        </section>
        {doc === "quote" ? (
          <section>
            <p className="ps-cap">{customer?.name ? t("quote.page.greeting", { name: customer.name }) : t("quote.page.greetingNoName")}</p>
            <p className="ps-letter">{t("workOrder.print.quoteIntro")}</p>
          </section>
        ) : (
          <section>
            <p className="ps-cap">{t("workOrder.customer")}</p>
            <KeyValues
              rows={[
                [t("customer.name"), customer?.name ?? order.customerName],
                [t("customer.addressLine"), address],
                [t("customer.mobile"), phone, "mono"],
                [t("customer.email"), customer?.email],
                [t("customer.taxNumber"), customer?.taxNumber, "mono"],
              ]}
            />
          </section>
        )}
      </div>

      {order.customerRequest && doc !== "quote" && (
        <section className="ps-text">
          <p className="ps-cap">{t("workOrder.customerRequest")}</p>
          <p>{order.customerRequest}</p>
        </section>
      )}
      {order.findings && doc !== "intake" && (
        <section className="ps-text">
          <p className="ps-cap">{t("workOrder.findings")}</p>
          <p>{order.findings}</p>
        </section>
      )}
      {doc === "intake" && (
        <section className="ps-ready">
          <div>
            <p className="ps-cap">{t("workOrder.expectedReadyAt")}</p>
            {/* the intake receipt must show the expected completion date (HU 249/2004. 5. §) */}
            <div className="ps-big">{order.expectedReadyAt ? date(order.expectedReadyAt) : "–"}</div>
          </div>
          {damagePhotos > 0 && <p className="ps-photos">{t("workOrder.print.damagePhotos", { count: damagePhotos })}</p>}
        </section>
      )}
      {doc === "intake" && shots.length > 0 && (() => {
        const c = intakeCollage(shots);
        const first = shots[0]!.createdAt;
        const works = link && link.expiresAt > new Date().toISOString();
        return (
          <PrintCollage
            shots={c.shown}
            more={c.more}
            caption={<>
              {t("intake.print.caption", { count: shots.length, date: dateTime(first) })}
              {works && <> {t("intake.print.onLink")} <span className="mono">{intakeLink(link!.token).replace(/^https?:\/\//, "").slice(0, 40)}…</span></>}
            </>}
          />
        );
      })()}

      {items.length > 0 && (
        <table className="ps-items">
          <thead>
            <tr>
              <th>{t("workOrder.item.description")}</th>
              <th>{t("workOrder.item.partNumber")}</th>
              <th className="num">{t("workOrder.item.quantity")}</th>
              <th className="num">{t("workOrder.item.unitPrice")}</th>
              <th className="num">{t("workOrder.item.vatRate")}</th>
              <th className="num">{t("workOrder.net")}</th>
              <th className="num">{t("workOrder.gross")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className={doc === "quote" && i.approval === "declined" ? "ps-declined" : undefined}>
                <td>
                  <span className="ps-desc">{i.description}</span>
                  {i.customerSupplied && <span> ({t("workOrder.item.customerSuppliedShort")})</span>}
                  {i.approval && i.approval !== "none" && (doc === "quote" || i.approval === "pending") && (
                    <span className={`ps-mark ps-mark-${i.approval}`}>{t(`quote.approval.${i.approval}`)}</span>
                  )}
                </td>
                <td className="mono">{i.partNumber}</td>
                <td className="num">
                  {i.quantity.toLocaleString(i18n.language)} {i.unit}
                </td>
                <td className="num">
                  {money(i.unitPrice)} {t(`workOrder.priceMode.${i.priceMode}Short`)}
                </td>
                <td className="num">{i.vatRate}%</td>
                <td className="num">{money(i.net)}</td>
                <td className="num">{money(i.gross)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {items.length > 0 && (
        <div className="ps-sum">
          <div className="ps-sum-left">
            {doc === "closed" && <Stamp label={t("workOrder.print.stampClosed")} when={dateTime(order.closedAt)} />}
            {doc === "quote" && answered?.answeredAt && (
              <Stamp
                tone={quoted.some((i) => i.approval === "pending") ? "orange" : "green"}
                label={t("workOrder.print.stampAnswered")}
                when={dateTime(answered.answeredAt)}
              />
            )}
            {doc === "closed" && answered?.answeredAt && (
              <p className="ps-note">
                {t("quote.printAnswered", { date: date(answered.answeredAt), via: t(`quote.via.${answered.channel}`) })}
                {answered.note && <> · {answered.note}</>}
              </p>
            )}
          </div>
          <DocTotals
            rows={[
              { label: t("workOrder.net"), value: money(totals.net) },
              { label: t("workOrder.vat"), value: money(totals.vat) },
            ]}
            label={grandLabel}
            total={money(totals.gross)}
          />
        </div>
      )}

      {qr && (
        <section className="ps-qr">
          <div className="ps-qr-code" dangerouslySetInnerHTML={{ __html: renderSVG(qr.link, { border: 1 }) }} />
          <div>
            <div className="ps-qr-title">{t("workOrder.print.quoteQrTitle")}</div>
            <p>{t("workOrder.print.quoteQrText")}</p>
            <p className="mono ps-link">{qr.link}</p>
          </div>
        </section>
      )}
      {doc === "quote" && <p className="ps-note">{t("workOrder.print.quoteNote")}</p>}

      {order.notes && doc !== "quote" && (
        <section className="ps-text ps-text-plain">
          <p className="ps-cap">{t("workOrder.notes")}</p>
          <p>{order.notes}</p>
        </section>
      )}
      {g("printNote") && <p className="ps-note">{g("printNote")}</p>}

      {doc === "intake" && (
        <div className="ps-stamp-row">
          <Stamp tone="orange" label={t("workOrder.print.stampIntake")} when={dateTime(order.receivedAt)} />
        </div>
      )}

      {/* the statutory text and the signatures sit at the bottom of the page */}
      <div className="ps-bottom">
        {doc === "closed" && w.applies && (
          <section className="ps-legal ps-warranty">
            <strong>{t("workOrder.print.warrantyTitle")}</strong> {w.own ?? t(`legal.${w.profile.textKey}.warranty`)}{" "}
            {t("legal.handoverFacts", { handover: date(w.handover), km: kmText ?? "–" })}
            {w.until && <> · <strong>{t("legal.warrantyUntil", { until: date(w.until) })}</strong></>}
          </section>
        )}
        {doc === "intake" && <p className="ps-legal">{t(`legal.${w.profile.textKey}.intake`)}</p>}
        <Signatures
          left={t(doc === "closed" ? "workOrder.print.signHandover" : doc === "quote" ? "workOrder.print.signQuoteWorkshop" : "workOrder.print.signOrder")}
          right={t(doc === "closed" ? "workOrder.print.signHandoverWorkshop" : doc === "quote" ? "workOrder.print.signQuoteCustomer" : "workOrder.print.signReceiver")}
          leftAbove={phoneSig && <PrintSignature sig={phoneSig} when={dateTime(phoneSig.signedAt)} />}
        />
        {phoneSig && <p className="ps-legal ps-sig-note">{t("intake.print.hashNote", { hash: shortHash(phoneSig.contentHash) })}</p>}
        {foot}
      </div>
    </div>
  );
}

/** The phone signature image above the customer's signature line, with "telefonon aláírva <date time>". */
function PrintSignature({ sig, when }: { sig: WorkOrderSignature; when: string }) {
  const { t } = useTranslation();
  return (
    <span className="ps-phone-sig">
      {sig.imageUrl && <img src={sig.imageUrl} alt={t("signature.imageAlt", { name: sig.signerName })} />}
      <small className="mono">{t("intake.print.signedOnPhone", { date: when })}</small>
    </span>
  );
}

/** The handover signed on the phone, in the closed band. */
function HandoverNote({ sig, order, customer, shots }: { sig: WorkOrderSignature; order: WorkOrder; customer: Customer | null; shots: IntakeShot[] }) {
  const matches = useSignatureMatches(sig, order, customer, shots);
  return <SignedNote sig={sig} matches={matches} />;
}

/**
 * The new work order tells its page to open the signature pad once (the customer is still at the counter), and
 * whether the customer's e-mail waits for the end of the signing step.
 */
let autoSignFor: { id: string; send: boolean } | null = null;
function markAutoSign(id: string, send: boolean) {
  autoSignFor = { id, send };
}
function takeAutoSign(id: string): { sign: boolean; send: boolean } {
  if (autoSignFor?.id !== id) return { sign: false, send: false };
  const send = autoSignFor.send;
  autoSignFor = null;
  return { sign: true, send };
}
